import React, { FC, useContext, useState, useCallback, useEffect } from 'react';
import get from 'lodash/get';
import noop from 'lodash/noop';
import set from 'lodash/set';
import { useDispatch } from 'react-redux';

import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { Content, ExternalLinkButton, Row } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import FormContext from 'src/presentation/contexts/FormContext';
import { getAdminURL } from 'src/presentation/components/pages/shared/utils/Utils';
import { IvrEditorContext } from 'src/presentation/components/pages/ivr/IvrEditor';
import InputField from 'src/presentation/components/forms/InputField';
import ivrResource from 'src/actions/ivrs/ivrResource';
import {
  DESCRIPTION_LABEL_TEXT,
  DESCRIPTION_PLACEHOLDER_TEXT,
  NAME_PLACEHOLDER_TEXT,
  NodeProps,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import SaveSelectorFormConfirmationModal from './SaveSelectorFormConfirmationModal';

type SelectorFormProps = {
  nodeProps: NodeProps;
  saveConfirmationTitle: string;
};

const SelectorForm: FC<SelectorFormProps> = ({ nodeProps, saveConfirmationTitle }) => {
  const { attr, disabled, readOnly } = nodeProps;
  const { setFormIsValid } = useContext(IvrEditorContext);

  const channelsEntryPointsUrl = getAdminURL('entry-points');
  const SelectionDescription = <SelectionDescriptionForEntryPoints dataAid={attr} url={channelsEntryPointsUrl} />;
  const channelsEntryPointsButtonText = 'Go to Entry Points';

  const dispatch = useDispatch();
  const { validateNode: validate } = ivrResource.effects;
  const { resetErrors } = ivrResource.actions;
  const { workingState, onBlur, updateState } = useContext(FormContext);

  const [baseAttr, setBaseAttr] = useState('');
  const [nodeId, setNodeId] = useState('');

  const description = get(workingState, `${attr}.description`, '');
  const [isNew, setIsNew] = useState(containsZeroWidthSymbol(description));
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const selectionValue = get(workingState, `${attr}.selection`, '');
  const dialNumber = get(workingState, `${attr}.number`);
  const id = get(workingState, `${attr}.id`, '');

  const [input, setInput] = useState('');

  useEffect(() => {
    setInput(selectionValue);
  }, [selectionValue, setInput]);

  useEffect(() => {
    if (attr !== baseAttr && nodeId !== id) {
      setBaseAttr(attr);
      setNodeId(id);
      setIsNew(description?.length === 1 && containsZeroWidthSymbol(description));
    }
  }, [attr, selectionValue, description, id, nodeId, baseAttr, setBaseAttr, setNodeId]);

  const onSave = useCallback(() => {
    setShowSaveConfirmation(false);
    setIsNew(false);

    const currentState = { ...workingState };
    const description = get(currentState, `${attr}.description`, '');
    set(currentState, `${attr}.description`, removeZeroWidth(description));

    onBlur && onBlur({ currentState });
  }, [attr, workingState, onBlur, setIsNew]);

  const onOk = () => {
    resetErrors({});
    const itemId: any = get(workingState, `${attr}.id`);

    // on UI we doesn't use name field that's why we need to set it
    updateState(
      set({}, attr, {
        selection: input,
        name: get(workingState, `${attr}.description`, ''),
      })
    );

    const isConfigValid = !!dispatch(validate(workingState, itemId));
    setFormIsValid(itemId, isConfigValid);

    if (isConfigValid) {
      setShowSaveConfirmation(true);
    }
  };

  const renderChannelsPageLink = () => (
    <ExternalLinkButton data-aid="goToChannels" href={channelsEntryPointsUrl} target="_blank">
      {channelsEntryPointsButtonText}
    </ExternalLinkButton>
  );

  const renderOkButton = () => (
    <Button data-aid="saveButton" disabled={disabled} onClick={onOk} type={ButtonTypes.PRIMARY}>
      Ok
    </Button>
  );

  const handleChangeSelection = value => setInput(value);

  const renderButton = isNew ? renderOkButton() : renderChannelsPageLink();
  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.selection`}
            data-aid="selection"
            description={SelectionDescription}
            disabled={!isNew || disabled}
            label="Name"
            onBlur={isNew ? noop : undefined}
            onChange={handleChangeSelection}
            readOnly={!isNew || readOnly}
            value={input}
            placeholder={NAME_PLACEHOLDER_TEXT}
          />
        </Row>
        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid="description"
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            onBlur={isNew ? noop : undefined}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>

        {dialNumber && (
          <Row>
            <InputField attr={`${attr}.number`} data-aid="number" disabled={true} label="Number" readOnly={true} />
          </Row>
        )}

        <Row>{renderButton}</Row>
      </Content>
      {showSaveConfirmation && (
        <SaveSelectorFormConfirmationModal
          title={saveConfirmationTitle}
          onOk={onSave}
          onCancel={() => setShowSaveConfirmation(false)}
        />
      )}
    </>
  );
};

const SelectionDescriptionForEntryPoints = ({ dataAid, url }) => (
  <div>
    This is the name shown in <ExternalLink data-aid={`${dataAid}-goToChannels-link`} label="Entry Points" to={url} />,
    where you can map this node to an Inbox of your choice. Once you save a name, it cannot be changed.
  </div>
);

const ZERO_WIDTH_SPACES_REGEX = /([\u200B]+|[\u200C]+|[\u200D]+|[\u200E]+|[\u200F]+|[\uFEFF]+)/g;

function removeZeroWidth(stringToTrim: string): string {
  return stringToTrim.replace(ZERO_WIDTH_SPACES_REGEX, '');
}

function containsZeroWidthSymbol(stringToTrim): boolean {
  return removeZeroWidth(stringToTrim).length !== stringToTrim.length;
}

export default SelectorForm;
